import { Dimensions } from './hooks/useWindowDimensions';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Piece from './Piece';
import Spinner, { getSpinnerSize } from './Spinner';
import { animated, config, useSpring } from '@react-spring/web';
import { PuzzleData, PuzzleSize, Theme } from './Themes';
import BackSvg from './svg/BackSvg';
import useSound from 'use-sound';
import startSfx from './sounds/start.mp3';
import helpSfx from './sounds/rotate.mp3';
import takeSfx from './sounds/take.mp3';
import dropSfx from './sounds/drop.mp3';
import winSfx from './sounds/win.mp3';
import HelpSvg from './svg/HelpSvg';
import SvgButton from './SvgButton';
import { createUseGesture, dragAction } from '@use-gesture/react';

type PuzzleProps = {
	puzzle: PuzzleData;
	size: PuzzleSize;
	onPuzzleComplete: () => void;
	onBackClick: () => void;
	theme: Theme;
	windowDim: Dimensions;
};

const Puzzle = ({ puzzle: { name }, size, onPuzzleComplete, onBackClick, theme, windowDim }: PuzzleProps) => {
	const [playStartSfx] = useSound(startSfx);
	const [playHelpSfx] = useSound(helpSfx);
	const [playTakeSfx] = useSound(takeSfx);
	const [playDropSfx] = useSound(dropSfx);
	const [playWinSfx] = useSound(winSfx);

	const [isLoading, setIsLoading] = useState(true);
	const [isLoadError, setIsLoadError] = useState(false);
	const [isHelpShow, setIsHelpShow] = useState(false);
	const [isComplete, setIsComplete] = useState(false);
	const wasComplete = useRef(false);

	const loadedImages = useRef(new Set());
	const onLoadImage = useCallback(
		(i: number) => {
			loadedImages.current.add(i);
			if (isLoading && loadedImages.current.size === size.x * size.y) {
				// setTimeout(() => setIsLoading(false), 1000);
				setIsLoading(false);
				playStartSfx();
			}
		},
		[loadedImages, isLoading, size, playStartSfx]
	);
	const onLoadError = useCallback(() => {
		setIsLoading(false);
		setIsLoadError(true);
	}, []);
	const spinnerSize = useMemo(() => getSpinnerSize(windowDim, 300), [windowDim]);

	const onHelpClick = useCallback(() => {
		setIsHelpShow(true);
		playHelpSfx();
	}, [playHelpSfx]);
	useEffect(() => {
		if (isHelpShow) {
			const timer = setTimeout(() => setIsHelpShow(false), 2500);
			return () => clearTimeout(timer);
		}
	}, [isHelpShow]);

	const [imageAnimatedStyle, imageSpringApi] = useSpring(() => ({ opacity: 0, scale: 1.25 }), []);
	useEffect(() => {
		if (isComplete) {
			imageSpringApi.start({
				from: { opacity: 0, scale: 1 },
				to: { opacity: 1, scale: 1 },
			});
		} else {
			imageSpringApi.start({
				opacity: isHelpShow ? 1 : 0,
				scale: isHelpShow ? 1 : 1.25,
			});
		}
	}, [isComplete, isHelpShow, imageSpringApi]);

	const [pieces, setPieces] = useState<number[]>([]);
	useEffect(() => {
		setPieces(Array.from(Array(size.x * size.y).keys()).sort(() => Math.random() - 0.5));
	}, [size]);
	const zIndexMax = useRef(1);
	const pieceWidth = useMemo(
		() => Math.min(600 / Math.max(size.x, size.y), 0.9 * Math.min(windowDim.width / size.x, windowDim.height / size.y)),
		[windowDim, size]
	);
	const boardZoomScale = useMemo(
		() => Math.min(1.5, windowDim.width / (size.x * pieceWidth), windowDim.height / (size.y * pieceWidth)),
		[windowDim, size, pieceWidth]
	);

	const [boardSpringStyle, boardSpringApi] = useSpring(() => ({ x: 0, y: 0, opacity: 0, scaleX: 0.1, scaleY: 0.1, config: config.wobbly }));
	useEffect(() => {
		if (!isLoading) {
			boardSpringApi.start({ opacity: 1, scaleX: 1, scaleY: 1 });
		}
	}, [isLoading, boardSpringApi]);

	const boardClickTime = useRef(0);
	const useDragGesture = createUseGesture([dragAction]);
	const boardDrag = useDragGesture(
		{
			onDragStart: () => {
				playTakeSfx();
				boardClickTime.current = Date.now();
			},
			onDrag: ({ movement: [x, y] }) => {
				if (isComplete) {
					boardSpringApi.start({ x, y, scaleX: boardZoomScale, scaleY: boardZoomScale, config: config.stiff });
				} else {
					boardSpringApi.start({ x, y, scaleX: 1.07, scaleY: 0.93, config: config.stiff });
				}
			},
			onDragEnd: () => {
				const isTap = Date.now() - boardClickTime.current < 200;
				const delay = isTap ? (isComplete ? 1500 : 100) : 0;
				boardSpringApi.start({ x: 0, y: 0, scaleX: 1, scaleY: 1, config: config.wobbly, delay });
				if (!isComplete && !isTap) {
					playDropSfx();
				}
			},
		},
		{
			drag: {
				preventDefault: true,
				bounds: { left: 0, right: 0, top: 0, bottom: 0 },
				rubberband: 0.12,
			},
		}
	);

	useEffect(() => {
		if (isComplete && !wasComplete.current) {
			wasComplete.current = true;
			boardSpringApi.start({
				to: [
					{ scaleX: boardZoomScale, scaleY: boardZoomScale, delay: 300 },
					{ scaleX: 1, scaleY: 1, delay: 1500 },
				],
			});
			playWinSfx();
			const timer = setTimeout(() => {
				setPieces([]);
				onPuzzleComplete();
			}, 2000);
			return () => clearTimeout(timer);
		}
	}, [isComplete, name, onPuzzleComplete, playWinSfx, boardSpringApi, boardZoomScale, wasComplete]);

	const cells = useRef(new Array<number>(size.x * size.y).fill(0));
	const fillCell = useCallback((i: number, correct: boolean): boolean => {
		if (cells.current[i] > 0) {
			return false;
		} else {
			cells.current[i] = correct ? 2 : 1;
			// TOD Don't forget to fix this:
			// if (cells.current.filter((c) => c === 2).length === 1) {
			if (cells.current.filter((c) => c === 2).length === cells.current.length) {
				setIsComplete(true);
			}
			return true;
		}
	}, []);
	const emptyCell = useCallback((i: number): void => {
		cells.current[i] = 0;
	}, []);

	const buttonWidth = useMemo(() => (size.x * pieceWidth) / 8, [size, pieceWidth]);

	return (
		<>
			<Spinner size={spinnerSize} color={theme.primary.color} enabled={isLoading} />
			<div
				className="absolute select-none"
				style={{
					width: size.x * pieceWidth,
					height: (size.y * pieceWidth) / size.aspect,
					left: (windowDim.width - size.x * pieceWidth) / 2,
					top: (windowDim.height - (size.y * pieceWidth) / size.aspect) / 2,
					visibility: isLoading ? 'hidden' : 'visible',
				}}>
				<SvgButton
					Svg={BackSvg}
					width={buttonWidth}
					height={buttonWidth}
					position={windowDim.width - size.x * pieceWidth > 2 * buttonWidth ? 'left' : 'topLeft'}
					color={theme.background.text}
					onClick={onBackClick}
					enabled={true}
				/>
				{!isLoadError && (
					<SvgButton
						Svg={HelpSvg}
						width={buttonWidth / 1.5}
						height={buttonWidth}
						position={windowDim.width - size.x * pieceWidth > buttonWidth ? 'right' : 'topRight'}
						color={theme.background.text}
						onClick={onHelpClick}
						enabled={!isComplete && !isHelpShow}
					/>
				)}
				<animated.div
					{...boardDrag()}
					className="absolute h-full w-full touch-none overflow-hidden shadow-md md:shadow-lg lg:shadow-xl"
					style={{
						borderRadius: 0.25 * pieceWidth * size.aspect * size.aspect,
						backgroundColor: !isComplete ? theme.primary.color : undefined,
						zIndex: isComplete ? 2000 : 0,
						...boardSpringStyle,
					}}>
					<animated.div
						className="pointer-events-none absolute inset-0 bg-white"
						style={{
							...imageAnimatedStyle,
							borderRadius: 0.25 * pieceWidth * size.aspect * size.aspect,
							overflow: 'hidden',
						}}>
						<img
							src={`/images/${name}/image.jpg`}
							alt="completed"
							className="pointer-events-none h-full w-full"
							style={{ opacity: isComplete ? 1 : 0.5 }}
						/>
					</animated.div>
					{isLoadError && (
						<div
							className="pointer-events-none absolute inset-0 flex touch-none items-center justify-center font-karusel text-3xl"
							style={{ color: theme.primary.text }}>
							<p>Ошибка загрузки</p>
						</div>
					)}
				</animated.div>
				{!isLoadError &&
					pieces.map((id, i) => (
						<Piece
							key={'piece' + id}
							puzzleSize={size}
							overlap={size.overlap}
							pieceHeight={pieceWidth / size.aspect}
							image={`/images/${name}/${size.x}x${size.y}/${Math.floor(id / size.x) + 1}${(id % size.x) + 1}.png`}
							visible={!isLoading}
							enabled={!isComplete}
							{...{
								i,
								id,
								pieceWidth,
								windowDim,
								zIndexMax,
								fillCell,
								emptyCell,
								onLoadImage,
								onLoadError,
							}}
						/>
					))}
			</div>
		</>
	);
};

export default Puzzle;
